/**
 * Krok CER1 body 2 a 3 — ODESLANI CHYBY Z PROHLIZECE DO NASI DB.
 *
 * Fronta, deduplikace, strop a davkove odeslani na `POST /v1/client-errors`.
 * NIKAM JINAM: zadny Sentry, zadna sluzba tretí strany, zadny e-mail — cil je
 * jen nase vlastni API na stejnem originu (same-origin cookie session).
 *
 * PROC VEDLE `report-collector.ts` A NE V NEM: sberac (MOB1) drzi POSLEDNICH
 * 20 chyb V PAMETI pro tlacitko „Nahlásit problém" — pasivni kruhovy buffer,
 * ktery nic neposila. Tohle je prenos: fronta s casem, stropem a odeslanim.
 * Sberac tuhle funkci VOLA (`reportClientError`), takze sber zustava na jednom
 * miste a nevznikla druha kopie odposlechu `fetch`/`error`.
 *
 * TRI POJISTKY PROTI SMYCCE HLASENI O HLASENI:
 *   1. odeslani jde PUVODNIM `fetch` (sberac ho sem preda pri instalaci),
 *      takze nejde pres odposlech,
 *   2. sberac adresu `/v1/client-errors` navic preskakuje,
 *   3. selhani odeslani se NIKDY nezaznamenava jako udalost (jen se zahodi).
 *
 * BEZ PRIHLASENI SE NEPOSILA NIC (bod 2): zapina se az `enableClientErrorReporting`
 * po nacteni session a `disable…` pri odhlaseni.
 */
import { API_BASE_URL } from './api.js';
import { appVersion } from './pwa.js';
import {
  fingerprintOf,
  routeTemplateOf,
  scrubStack,
  scrubText,
  uaShort,
  type ClientErrorKind,
} from './client-error-scrub.js';

/** Cesta endpointu — `report-collector.ts` ji preskakuje pri odposlechu `fetch`. */
export const CLIENT_ERROR_ENDPOINT = '/v1/client-errors';

/** Nejdriv za 5 s po predchozim odeslani (bod 2). */
const FLUSH_INTERVAL_MS = 5_000;
/** Strop: 30 udalosti na 10 minut a zalozku (bod 2). */
export const MAX_EVENTS_PER_WINDOW = 30;
const WINDOW_MS = 600_000;
/** Server bere nejvyse 20 udalosti v davce (bod 3) — vic se odesle priste. */
export const MAX_BATCH = 20;
/** Tyz otisk se znovu posila nejdriv po minute (jinak jen pricita pocet). */
const RESEND_SAME_FINGERPRINT_MS = 60_000;

export interface ClientErrorInput {
  readonly kind: ClientErrorKind;
  readonly message: string;
  readonly stack?: string | undefined;
  readonly apiMethod?: string | undefined;
  readonly apiPath?: string | undefined;
  readonly apiStatus?: number | undefined;
  readonly errorCode?: string | undefined;
}

/** Jedna radka davky — presne to, co prijima zod schema routy. */
export interface ClientErrorPayload {
  readonly kind: ClientErrorKind;
  readonly fingerprint: string;
  readonly count: number;
  readonly message: string;
  readonly stack: string;
  readonly route: string;
  readonly entityId: string | null;
  readonly apiMethod: string | null;
  readonly apiPath: string | null;
  readonly apiStatus: number | null;
  readonly errorCode: string | null;
  readonly appVersion: string;
  readonly uaShort: string;
  readonly viewport: string;
}

type TransportFetch = (input: string, init: RequestInit) => Promise<unknown>;

let enabled = false;
let transport: TransportFetch | null = null;
let pending: ClientErrorPayload[] = [];
/** Casy prijatych udalosti pro klouzave okno stropu. */
let acceptedAt: number[] = [];
/** Kdy byl otisk naposledy odeslan — proti zaplaveni stejnou chybou. */
const lastSentAt = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lastFlushAt = 0;
let onPageHide: (() => void) | null = null;

function now(): number {
  return Date.now();
}

function viewport(): string {
  if (typeof window === 'undefined') return '';
  return `${String(window.innerWidth)}x${String(window.innerHeight)}`;
}

/** Strop okna. `true` = udalost se zahazuje (a nikdo se o tom nedozvi). */
function overQuota(at: number): boolean {
  acceptedAt = acceptedAt.filter((stamp) => at - stamp < WINDOW_MS);
  return acceptedAt.length >= MAX_EVENTS_PER_WINDOW;
}

/**
 * Prijme chybu. NIKDY nevyhazuje a nikdy nic nevraci — volajici (odposlech
 * `fetch`, error boundary, hlaska v UI) nesmi na hlaseni cekat ani na nem
 * selhat.
 */
export function reportClientError(input: ClientErrorInput): void {
  if (!enabled || typeof window === 'undefined') return;
  try {
    const at = now();
    const { route, entityId } = routeTemplateOf(window.location.pathname);
    const message = scrubText(input.message);
    const fingerprint = fingerprintOf({
      kind: input.kind,
      route,
      message,
      apiMethod: input.apiMethod ?? null,
      apiPath: input.apiPath ?? null,
      apiStatus: input.apiStatus ?? null,
      errorCode: input.errorCode ?? null,
    });

    // Tataz chyba znovu ve FRONTE — jen pocitadlo, ne druha radka (bod 2).
    const queued = pending.findIndex((event) => event.fingerprint === fingerprint);
    if (queued >= 0) {
      const found = pending[queued];
      if (found !== undefined) pending[queued] = { ...found, count: found.count + 1 };
      return;
    }
    // Tataz chyba znovu po odeslani — do minuty se neposila vubec.
    const sentAt = lastSentAt.get(fingerprint);
    if (sentAt !== undefined && at - sentAt < RESEND_SAME_FINGERPRINT_MS) return;
    if (overQuota(at)) return;
    acceptedAt.push(at);

    pending.push({
      kind: input.kind,
      fingerprint,
      count: 1,
      message,
      stack: scrubStack(input.stack),
      route,
      entityId,
      apiMethod: input.apiMethod ?? null,
      apiPath: input.apiPath ?? null,
      apiStatus: input.apiStatus ?? null,
      errorCode: input.errorCode ?? null,
      appVersion: appVersion(),
      uaShort: uaShort(window.navigator.userAgent),
      viewport: viewport(),
    });
    scheduleFlush();
  } catch {
    // Hlaseni chyby nesmi zpusobit chybu. Zadny zapis, zadne vyhozeni.
  }
}

function scheduleFlush(): void {
  if (flushTimer !== null || pending.length === 0) return;
  const wait = Math.max(0, FLUSH_INTERVAL_MS - (now() - lastFlushAt));
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushClientErrors();
  }, wait);
  // Timer nesmi drzet beh testu ani nativni obal.
  (flushTimer as unknown as { unref?: () => void }).unref?.();
}

/**
 * Posle az `MAX_BATCH` udalosti. `beacon = true` se pouziva pri odchodu ze
 * stranky (`pagehide`) — `navigator.sendBeacon` prezije zavreni zalozky,
 * bezny `fetch` ne.
 */
export async function flushClientErrors(beacon = false): Promise<void> {
  if (!enabled || pending.length === 0) return;
  const batch = pending.slice(0, MAX_BATCH);
  pending = pending.slice(MAX_BATCH);
  lastFlushAt = now();
  for (const event of batch) lastSentAt.set(event.fingerprint, lastFlushAt);
  const url = `${API_BASE_URL}${CLIENT_ERROR_ENDPOINT}`;
  const body = JSON.stringify({ events: batch });
  try {
    if (beacon && typeof navigator.sendBeacon === 'function') {
      // `type: 'application/json'` — bez nej posila prohlizec text/plain
      // a Fastify by telo neparsoval (odpoved by byla 415).
      const sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      if (sent) return;
    }
    const send = transport ?? ((input: string, init: RequestInit) => globalThis.fetch(input, init));
    await send(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Pojistka 3: selhani odeslani se zahazuje. Kdyby se zapsalo jako
    // udalost, vznikla by nekonecna smycka hlaseni o hlaseni.
  } finally {
    if (pending.length > 0) scheduleFlush();
  }
}

export interface ClientErrorReporterOptions {
  /** PUVODNI `fetch` (pred odposlechem) — preda ho `installReportCollector`. */
  readonly transport?: TransportFetch | undefined;
}

/** Zapne odesilani. Volat az po prihlaseni; idempotentni. */
export function enableClientErrorReporting(options: ClientErrorReporterOptions = {}): void {
  if (typeof window === 'undefined') return;
  if (options.transport !== undefined) transport = options.transport;
  if (enabled) return;
  enabled = true;
  onPageHide = () => {
    void flushClientErrors(true);
  };
  window.addEventListener('pagehide', onPageHide);
}

/** Vypne odesilani (odhlaseni) a zahodi frontu — bez session se neposila nic. */
export function disableClientErrorReporting(): void {
  enabled = false;
  pending = [];
  acceptedAt = [];
  lastSentAt.clear();
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (typeof window !== 'undefined' && onPageHide !== null) {
    window.removeEventListener('pagehide', onPageHide);
  }
  onPageHide = null;
}

/** Jen pro testy: co ceka ve fronte a jak je nastaveny prenos. */
export function clientErrorReporterStateForTests(): {
  readonly enabled: boolean;
  readonly pending: readonly ClientErrorPayload[];
} {
  return { enabled, pending: [...pending] };
}

/** Jen pro testy: uplny vychozi stav (vcetne casu posledniho odeslani). */
export function resetClientErrorReporterForTests(): void {
  disableClientErrorReporting();
  transport = null;
  lastFlushAt = 0;
}
